import React from 'react';

export const HelpGuide = ({ show, onClose }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h2 className="admin-title">PatentsPrism Guide</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>
        
        {/* Modal Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-bold mb-4">Welcome to PatentsPrism</h2>
            <p className="text-lg mb-4">Your comprehensive patent analysis platform.</p>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <p className="font-bold text-lg mb-2">Your Goal:</p>
              <p className="mb-2">By the end of this workflow, you'll have a complete strategic picture:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Landscape:</strong> Who's active and what they're doing</li>
                <li><strong>Opportunities:</strong> Where the white space is</li>
                <li><strong>Momentum:</strong> Which areas are growing</li>
                <li><strong>Influence:</strong> Who the key players are</li>
              </ul>
            </div>
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 1: Select Your Study</h3>
            <p>If you have multiple studies, you'll see a <strong>Study dropdown</strong> in the top right of the ribbon. Select the patent portfolio you want to analyze. If you have only one study, it's already loaded.</p>
            
            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 2: Understanding the Controls and Features</h3>
            <p className="mb-3">The ribbon adapts based on your current view. Here's what you'll see:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Views Dropdown:</strong> Switch between different analysis views within each tab</li>
              <li><strong>Applicant Search:</strong> Filter the entire analysis to show only patents from specific companies</li>
              <li><strong>Text Search:</strong> Search across all patent text fields (titles, abstracts, claims).
                <div className="mt-2 mb-2">
                  <strong>Use quotes for exact phrase:</strong> Example: "machine learning"
                </div>
                <div className="mb-2">
                  <strong>Boolean operators:</strong>
                  <ul className="list-disc pl-6 mt-1 space-y-1">
                    <li><strong>AND operator (+):</strong> cat + dog (searches for cat and dog)</li>
                    <li><strong>OR operator (|):</strong> cat | dog (searches for cat or dog)</li>
                    <li><strong>NOT operator (-):</strong> cat - dog (searches for cat without dog)</li>
                  </ul>
                </div>
                <div className="mb-2">
                  <strong>Wildcard operator (*):</strong> invert* matches inverter, inversion, inverted, etc.
                </div>
                <div className="mb-2">
                  <strong>Acronyms work perfectly:</strong> Search is case-insensitive. Example: AI matches "AI system", "using AI", etc.
                </div>
                <div className="text-sm italic text-gray-600">
                  Note: Space between words is treated as AND operator. Plain terms match whole words only (driver matches "driver" but not "driverless"). Use wildcards for partial matches (driver* matches both).
                </div>
              </li>
              <li><strong>Count Toggle:</strong> Switch between "Original" and "Individual" counting modes:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li><strong>Original:</strong> Joint patents counted once (e.g., "Company A; Company B" = 1 patent)</li>
                  <li><strong>Individual:</strong> Joint patents counted for each applicant (e.g., "Company A; Company B" = 1 for Company A + 1 for Company B)</li>
                </ul>
                <div className="text-sm italic text-gray-600 mt-1">
                  Applies to: Basic tab (Applicants view) and Trends tab (Applicants charts)
                </div>
              </li>
              <li><strong>N Slider:</strong> Limit visualizations to show only the top N results</li>
              <li><strong>Genus Filter</strong> (Innovation tab only): Focus on specific technology categories</li>
              <li><strong>Date Field Selector</strong> (Trends tab only): Choose Filing Date, Priority Date, or Publication Date</li>
              <li><strong>Flagging Patents:</strong> Mark important patents by clicking the star (☆) icon in any patent table. Flagged items show a filled star (⭐) and appear in My Collection, where you can add notes and export to CSV. Available across R&D Explorer, Whitespace, Deep Dives, and all Advanced views.</li>
            </ul>
            
            <p className="mt-4 text-sm italic">💡 <strong>Tip:</strong> Use the hover tip throughout the app to see interesting details on visualizations.</p>
            
            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 3: BASIC Tab - Understand the Landscape</h3>
            <p className="italic mb-3"><strong>Purpose:</strong> See who's active in this space and how organizations collaborate.</p>
            <p className="font-semibold mb-2">3 Views:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Applicants:</strong> Sortable table of all applicants with patent counts and statistics</li>
              <li><strong>Collaborations:</strong> Network diagram revealing which organizations work together on patents</li>
              <li><strong>R&D Preview:</strong> Treemap showing top applicants by patent volume</li>
            </ol>
            <p className="mt-3"><strong>What to learn:</strong> Who are the major players? Who collaborates with whom? What are the dominant R&D problem areas?</p>
            
            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 4: INNOVATION Tab - Find Opportunities</h3>
            <p className="italic mb-3"><strong>Purpose:</strong> Explore the technology landscape and identify white space opportunities.</p>
            <p className="font-semibold mb-2">3 Views:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>R&D Explorer:</strong> Treemap of problem categories. Click any box to drill down into technical approaches (solution methods)</li>
              <li><strong>Whitespace:</strong> Heatmap showing innovation density at the intersection of problems and solutions. <strong>Dark cells = crowded space. Light cells = potential white space. Thick blue borders = high citation impact.</strong> Look for light cells with blue borders—these are high-value opportunities</li>
              <li><strong>Deep Dives:</strong> Expandable table showing the detailed relationship between problem categories and technical approaches, with patent lists for each intersection</li>
            </ol>
            <p className="mt-3"><strong>Use the filters:</strong> Combine Genus filter, Applicant search, and Text search to focus your analysis.</p>
            <p className="mt-2 text-sm italic">💡 <strong>Tip:</strong> Text search in R&D Explorer displays a 'Matching Patents' table below the treemap, grouped by problem categories.</p>
            
            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 5: TRENDS Tab - Track Momentum</h3>
            <p className="italic mb-3"><strong>Purpose:</strong> See how innovation activity evolves and identify strategic timing opportunities.</p>
            
            <p className="font-semibold mb-2">3 Views:</p>
            <ol className="list-decimal pl-6 space-y-2 mb-4">
              <li><strong>Filing Trends:</strong> Track patent activity over time across different groupings</li>
              <li><strong>Expected Expiration:</strong> Visualize when patents expire to understand portfolio lifecycle</li>
              <li><strong>Patent dates:</strong> Look up specific patents by number to see their filing dates</li>
            </ol>
            
            <p className="font-semibold mb-2">Filing Trends View:</p>
            <p className="mb-2">Use the <strong>"Group by"</strong> dropdown to switch between two views:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Overall:</strong> 4 charts — patents, top 5 applicants, R&D areas, and tech levers over time</li>
              <li><strong>Specific R&D Area:</strong> 3 charts — patents, top 5 applicants, and tech levers for that area over time</li>
            </ul>
            <p className="mt-3 text-sm italic text-gray-600">Click the legend to show/hide specific items. Use the Applicant search to filter by company.</p>
            
            <p className="font-semibold mb-2 mt-4">Expected Expiration View:</p>
            <p className="mb-3">Analyze patent portfolio lifecycle through three complementary charts:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Granted vs Applications by Filing Year:</strong> Shows filing activity over time, split between granted patents and pending applications. Reveals portfolio age and prosecution pipeline.</li>
              <li><strong>Granted Patents - Expected Expiry:</strong> When granted patents will expire (filing date + 20 years). Red line marks current year—left shows expired patents, right shows remaining portfolio value.</li>
              <li><strong>Applications - Projected Expiry:</strong> When applications would expire if granted (filing date + 20 years). Indicates urgency for obtaining grants before deadlines.</li>
            </ol>
            
            <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-500">
              <p className="font-bold text-sm mb-2">⚠️ Important Disclaimer:</p>
              <p className="searchable-field-label">All expiration data should be verified independently. Actual expiry dates may differ due to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
                <li>Terminal disclaimers that shorten patent term</li>
                <li>Patent term adjustments (PTA) or extensions (PTE)</li>
                <li>Maintenance fee lapses or abandonments</li>
                <li>Supplementary Protection Certificates (SPCs)</li>
                <li>Jurisdiction-specific rules and exceptions</li>
              </ul>
              <p className="text-sm mt-2 italic">This view provides estimated timelines based on standard 20-year terms from filing date. Always consult official patent office records for definitive expiry dates.</p>
            </div>
            
            <p className="font-semibold mb-2 mt-4">Patent dates View:</p>
            <p className="mb-3">Enter comma-separated patent numbers (e.g., US12345678,EP98765) to look up specific patents. Results appear in a table showing Patent No, Title, Applicant, R&D area, Filing Date, and Expected Expiry.</p>
            
            <p className="mt-3"><strong>What to learn:</strong> Is your white space growing or shrinking? Which competitors are accelerating? Which technologies have momentum? When do key patents expire?</p>
            
            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 6: ADVANCED Tab - Measure Impact & Value</h3>
            <p className="italic mb-3"><strong>Purpose:</strong> Identify influential players and assess patent portfolio value.</p>
            <p className="font-semibold mb-2">5 Views:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Impact Scores:</strong> Forward Citations / Patent Age. High scores = influential innovations that others build upon. Threshold shown is the 75th percentile of the current dataset.</li>
              <li><strong>Velocity Scores:</strong> Forward Citations / Patent Age (filtered to recent patents). Shows emerging hotspots and fast-moving innovations. Threshold shown is the 75th percentile of the current dataset.</li>
              <li><strong>Strength Index:</strong> PatentsPrism proprietary score combining citation impact, family size, and backward citations. Scores are relative to your portfolio — threshold shown is the 75th percentile of the current dataset.</li>
              <li><strong>Dense Navigators:</strong> Patents with high backward citations. Shows innovations building on extensive prior art. Threshold shown is the 75th percentile of the current dataset.</li>
              <li><strong>Patent Valuation:</strong> PatentsPrism proprietary scoring based on citation impact, family size, remaining life, and grant status. Applications score 50% of granted patents. Choose from 4 methods to emphasize what matters most to you:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li><strong>Standard:</strong> Balanced assessment</li>
                  <li><strong>Citation Focused:</strong> Emphasizes innovation impact</li>
                  <li><strong>Family Size:</strong> Emphasizes depth and breadth of R&D development across patent filings</li>
                  <li><strong>Lifetime Value:</strong> Emphasizes remaining revenue potential</li>
                </ul>
                Scores ranked by percentile within your portfolio: Top 25% = Very High, 50-75% = High, 25-50% = Medium, Bottom 25% = Low.
              </li>
            </ol>
            <p className="mt-3"><strong>What to learn:</strong> Who produces the most influential work? Which patents are momentum drivers? Where is the highest-value innovation happening? What's your portfolio worth?</p>

            <hr className="my-8" />
            
            <h3 className="text-xl font-bold mt-8 mb-3">Step 7: Summarize & Build Your Strategy</h3>
            <p className="mb-3">You now have the complete picture from Steps 3-6. Synthesize your findings:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Cross-reference opportunities with trends:</strong> Are your white space areas growing or shrinking?</li>
              <li><strong>Validate with citations:</strong> Do high-impact players operate in your target areas?</li>
              <li><strong>Identify strategic gaps:</strong> Where are competitors absent or slowing down?</li>
              <li><strong>Spot emerging signals:</strong> Which areas show velocity but low crowding?</li>
            </ul>
            <p className="font-semibold mt-4">Use these insights to guide:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>R&D investment decisions and resource allocation</li>
              <li>Patent licensing and acquisition strategies</li>
              <li>Competitive positioning and differentiation</li>
              <li>Technology roadmapping and innovation planning</li>
            </ul>
            
            <div className="mt-8 p-4 bg-blue-50 rounded">
              <p className="study-name">Questions? Contact: vijaya.sarathy@patentsprism.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
